import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useTargetUidPayload } from './useTargetUidPayload';

export interface TelnyxConnectionRow {
  id: string;
  friendlyName: string;
  voiceUrl: string;
  active: boolean;
  /** True if this Telnyx Voice-Connection routes to the CP-3 voice agent. */
  ownedByCp3: boolean;
}

/** Lists the operating user's Telnyx TeXML applications. */
export function useTelnyxConnections(enabled: boolean) {
  const adminPayload = useTargetUidPayload();
  return useQuery<TelnyxConnectionRow[]>({
    queryKey: ['telnyx-connections', adminPayload.uid ?? null] as const,
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<{ uid?: string }, { items: TelnyxConnectionRow[] }>(
        functions,
        'listTelnyxConnections',
      );
      const r = await fn(adminPayload);
      return r.data.items;
    },
  });
}

/**
 * Creates a fresh TeXML application pointing at our voice-agent webhook
 * and stores its ID in the operating user's PhoneSettings.
 */
export function useCreateTelnyxConnection() {
  const qc = useQueryClient();
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable<
        { uid?: string },
        { ok: true; id: string; friendlyName: string; voiceUrl: string }
      >(functions, 'createTelnyxConnection');
      const r = await fn(adminPayload);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['telnyx-connections'] });
      qc.invalidateQueries({ queryKey: ['phoneSettings'] });
    },
  });
}
